import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LessonScreenPlayer from "./LessonScreenPlayer";

// Dark rigorous token set — matches presentation_mode:"rigorous" in all Cold War lessons
const TOK = {
  bg: "#12141C",
  surface: "#1B1E28",
  card: "#232636",
  border: "#2C3048",
  text: "#E8EEFF",
  textMid: "#8A9AC0",
  textDim: "#5A6A90",
  radius: 14,
  headingFont: "'Space Grotesk', 'Plus Jakarta Sans', sans-serif",
  bodyFont: "'Hanken Grotesk', 'Plus Jakarta Sans', sans-serif",
  labelFont: "'Space Mono', monospace",
};

// Strip "The Cold War N: " prefix that unit lesson titles include
function shortTitle(title) {
  return title.replace(/^.*?:\s*/, "");
}

function yearLabel(year) {
  return year ? year.replace("year", "Year ") : "";
}

export default function UnitPlayer({ unit, lessonMeta, onClose }) {
  const storageKey = `lumio_unit_${unit.unit_id}`;
  const playingKey = `lumio_playing_${unit.unit_id}`;
  const lessons = unit.lessons ?? [];
  const accent = lessonMeta?.color || lessons[0]?.color || "#F59E0B";

  const [playingIndex, setPlayingIndex] = useState(() => {
    try {
      const pos = JSON.parse(localStorage.getItem(playingKey) || "null");
      if (pos && Number.isInteger(pos.idx) && pos.idx < lessons.length && Date.now() - (pos.savedAt || 0) < 24 * 60 * 60 * 1000) {
        return pos.idx;
      }
    } catch {}
    return null;
  });
  const [completed, setCompleted] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(storageKey) || "[]"));
    } catch {
      return new Set();
    }
  });

  function openLesson(index) {
    setPlayingIndex(index);
    try { localStorage.setItem(playingKey, JSON.stringify({ idx: index, savedAt: Date.now() })); } catch {}
  }

  function handleLessonClose(index) {
    // Only unlock next if LessonScreenPlayer marked the lesson as done
    // (it writes lumio_lesson_done_{id}='1' on the final screen)
    const lesson = lessons[index];
    const isDone = localStorage.getItem(`lumio_lesson_done_${lesson.id}`) === "1";
    if (isDone) {
      const next = new Set(completed);
      next.add(index);
      localStorage.setItem(storageKey, JSON.stringify([...next]));
      setCompleted(next);
    }
    setPlayingIndex(null);
    try { localStorage.removeItem(playingKey); } catch {}
  }

  function canPlay(index) {
    return index === 0 || completed.has(index - 1);
  }

  const completedCount = lessons.filter((_, i) => completed.has(i)).length;
  const allDone = completedCount === lessons.length && lessons.length > 0;

  // ── Playing a lesson ───────────────────────────────────────────────
  if (playingIndex !== null) {
    const lesson = lessons[playingIndex];
    const fakeMeta = {
      id: lesson.id,
      subject: lesson.subject,
      year: lesson.year,
      title: lesson.title,
      color: lesson.color || accent,
      file: null,
    };
    return (
      <LessonScreenPlayer
        lesson={lesson}
        lessonMeta={fakeMeta}
        onClose={() => handleLessonClose(playingIndex)}
      />
    );
  }

  // ── Map view ───────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: "100vh",
        background: TOK.bg,
        color: TOK.text,
        fontFamily: TOK.bodyFont,
      }}
    >
      {/* Sticky header */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: TOK.surface,
          borderBottom: `1px solid ${TOK.border}`,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          onClick={onClose}
          style={{
            background: TOK.card,
            border: `1px solid ${TOK.border}`,
            borderRadius: 10,
            color: TOK.textMid,
            padding: "8px 14px",
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
            fontFamily: TOK.bodyFont,
          }}
        >
          ← Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 11,
              color: TOK.textDim,
              fontFamily: TOK.labelFont,
              textTransform: "uppercase",
              letterSpacing: ".08em",
            }}
          >
            {unit.subject} · {yearLabel(unit.year || lessons[0]?.year)}
          </p>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 700,
              color: TOK.text,
              fontFamily: TOK.headingFont,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {unit.title}
          </p>
        </div>
        <span
          style={{
            flexShrink: 0,
            fontSize: 11,
            color: accent,
            fontFamily: TOK.labelFont,
            fontWeight: 700,
            background: `${accent}1A`,
            borderRadius: 8,
            padding: "4px 10px",
          }}
        >
          {completedCount}/{lessons.length}
        </span>
      </div>

      {/* Progress bar */}
      <div style={{ height: 3, background: TOK.border }}>
        <motion.div
          style={{ height: "100%", background: accent }}
          animate={{
            width:
              lessons.length > 0
                ? `${(completedCount / lessons.length) * 100}%`
                : "0%",
          }}
          transition={{ type: "spring", stiffness: 120, damping: 22 }}
        />
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: "24px 16px 48px" }}>
        {/* Unit header */}
        <div style={{ marginBottom: 28 }}>
          <p
            style={{
              margin: "0 0 6px",
              fontSize: 11,
              color: accent,
              fontFamily: TOK.labelFont,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".1em",
            }}
          >
            Unit
          </p>
          <h1
            style={{
              margin: "0 0 10px",
              fontSize: 26,
              fontWeight: 800,
              color: TOK.text,
              fontFamily: TOK.headingFont,
              lineHeight: 1.2,
            }}
          >
            {unit.title}
          </h1>
          {unit.description && (
            <p
              style={{
                margin: 0,
                fontSize: 14,
                color: TOK.textMid,
                lineHeight: 1.65,
              }}
            >
              {unit.description}
            </p>
          )}
        </div>

        {/* All-done banner */}
        <AnimatePresence>
          {allDone && (
            <motion.div
              key="unit-done"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              style={{
                background: `${accent}18`,
                border: `1.5px solid ${accent}55`,
                borderRadius: TOK.radius,
                padding: "18px 20px",
                marginBottom: 24,
                textAlign: "center",
              }}
            >
              <p style={{ margin: "0 0 4px", fontSize: 28 }}>🎉</p>
              <p
                style={{
                  margin: "0 0 4px",
                  fontWeight: 800,
                  fontSize: 18,
                  color: TOK.text,
                  fontFamily: TOK.headingFont,
                }}
              >
                Unit complete
              </p>
              <p style={{ margin: 0, fontSize: 14, color: TOK.textMid }}>
                You've finished all {lessons.length} lessons. Review any time.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Lesson cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {lessons.map((lesson, i) => {
            const isDone = completed.has(i);
            const unlocked = canPlay(i);
            const isNext = !isDone && unlocked;

            return (
              <motion.div
                key={lesson.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => unlocked && openLesson(i)}
                style={{
                  background: isDone ? `${accent}0E` : TOK.card,
                  border: `1.5px solid ${
                    isDone
                      ? accent + "44"
                      : isNext
                      ? accent + "55"
                      : TOK.border
                  }`,
                  borderRadius: TOK.radius,
                  padding: "14px 16px",
                  cursor: unlocked ? "pointer" : "default",
                  opacity: unlocked ? 1 : 0.4,
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                }}
              >
                {/* State badge */}
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: isDone ? accent : isNext ? `${accent}22` : TOK.border,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: isDone ? 17 : 13,
                    color: isDone ? "#1A1000" : isNext ? accent : TOK.textDim,
                    fontWeight: 800,
                    fontFamily: TOK.labelFont,
                  }}
                >
                  {isDone ? "✓" : unlocked ? i + 1 : "🔒"}
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      margin: "0 0 2px",
                      fontSize: 11,
                      color: isDone ? accent : TOK.textDim,
                      fontFamily: TOK.labelFont,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: ".07em",
                    }}
                  >
                    Lesson {i + 1} of {lessons.length}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 15,
                      fontWeight: 700,
                      color: unlocked ? TOK.text : TOK.textDim,
                      fontFamily: TOK.headingFont,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {shortTitle(lesson.title)}
                  </p>
                  {lesson.estimated_minutes && (
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: 11,
                        color: TOK.textDim,
                        fontFamily: TOK.labelFont,
                      }}
                    >
                      ~{lesson.estimated_minutes} min
                    </p>
                  )}
                </div>

                {/* Arrow */}
                {unlocked && (
                  <div
                    style={{
                      flexShrink: 0,
                      fontSize: 12,
                      fontWeight: 700,
                      color: isDone ? accent : TOK.textMid,
                      fontFamily: TOK.labelFont,
                    }}
                  >
                    {isDone ? "Review →" : "Start →"}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Footer meta */}
        <div
          style={{
            marginTop: 32,
            paddingTop: 20,
            borderTop: `1px solid ${TOK.border}`,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 12,
              color: TOK.textDim,
              fontFamily: TOK.labelFont,
              lineHeight: 1.7,
            }}
          >
            {lessons.length} lessons ·{" "}
            {unit.curriculum?.framework || "Australian Curriculum v9.0"}
          </p>
        </div>
      </div>
    </div>
  );
}
